import React, { useEffect, useRef } from "react";
import { ActivityItem, CreateOriginFields } from "../../types";
import { Box, TextField } from "@mui/material";
import Datepicker from "@atoms/datepicker";
import AutocompleteDynamic from "@atoms/autocompleteDynamic";
import dayjs from "@helpers/dayjs";

function mapToOption(value: { id: number; name: string } | null) {
  return value ? { id: value.id, label: value.name } : null;
}

interface Props {
  setIsDirty: any;
  fields: (CreateOriginFields & Partial<ActivityItem>) | null;
  updateFieldsCallback: (updated: CreateOriginFields) => void;
}

const CreateOrigin: React.FC<Props> = ({
  setIsDirty,
  fields,
  updateFieldsCallback,
}) => {
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (fields?.execution_date) {
      const endDate = dayjs(fields.execution_date);
      if (
        !!fields.completion_date &&
        dayjs(fields.completion_date).isBefore(endDate)
      ) {
        updateFieldsCallback({ ...fields, completion_date: undefined });
      }
    } else {
      updateFieldsCallback({ ...fields, completion_date: null });
    }
  }, [fields?.execution_date]);

  const minDate = () => {
    const today = dayjs().startOf("day");
    const start = dayjs(fields?.execution_date);

    if (!start) return undefined;
    if (start.isBefore(today) || start.isSame(today)) {
      return today.endOf("day").toDate();
    }
    return start.endOf("day").toDate();
  };
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        gap: 2,
        display: "grid",
        gridAutoFlow: "row",
        justifyContent: "start",
        gridTemplateColumns: "repeat(3, 360px)",
      }}
    >
      <AutocompleteDynamic
        label="Supplier"
        multi={false}
        queryParam="name"
        initial={mapToOption(fields?.supplier || null)}
        updateFields={(value: any) => {
          setIsDirty(true);
          updateFieldsCallback({ ...fields, supplier_id: value });
        }}
        endpoint="/api/suppliers/names"
        sx={{ maxWidth: "360px" }}
      />
      <Datepicker
        isClearable={!!fields?.completion_date}
        start={fields?.execution_date ? new Date(fields.execution_date) : null}
        selectsRange={false}
        label="Starting Date"
        onChangeCallback={(value: string | null) => {
          setIsDirty(true);
          updateFieldsCallback({
            ...fields,
            execution_date: value || null,
          });
        }}
        size="medium"
        width="360px"
      />
      <Datepicker
        isClearable={true}
        minDate={minDate()}
        start={
          fields?.completion_date ? new Date(fields.completion_date) : null
        }
        selectsRange={false}
        label="Completion Date"
        onChangeCallback={(value: string | null) => {
          setIsDirty(true);
          updateFieldsCallback({
            ...fields,
            completion_date: value || null,
          });
        }}
        size="medium"
        width="360px"
      />
      <TextField
        className="note"
        multiline
        fullWidth
        sx={{ color: "black", gridColumn: "span 2" }}
        label="Note"
        value={fields?.comment || ""}
        onChange={(e) => {
          setIsDirty(true);
          updateFieldsCallback({
            ...fields,
            comment: e.target.value,
          });
        }}
      />
    </Box>
  );
};

export default CreateOrigin;
